import React from 'react';
import { motion } from 'motion/react';
import { ChevronRight } from 'lucide-react';
import { appUrl } from '../lib/domains';

const SECTIONS = [
  {
    eyebrow: "Dates and deadlines",
    heading: "Your next hearing date should not require a conversation with your clerk.",
    before: <>A senior advocate needs to confirm a hearing date.<br /><br />He checks the diary. Then asks the clerk. The clerk checks the file. The file is on a different table. The date exists somewhere, but getting to it requires three steps, two people, and a margin for error that should not exist in a professional practice.</>,
    withLexGat: <>Every case carries its own complete timeline. Next date. Stage. What it is fixed for. Which court. What needs to be prepared before that date.<br /><br />It is not a separate diary that you maintain alongside your cases. It is built into the case itself. When you open any matter you see exactly where it stands and exactly when you need to be back in court.</>,
    pullquote: "The diary does not wait for you to update it. The case updates the diary.",
  },
  {
    eyebrow: "Document drafting",
    heading: "The court heading. The parties block. The case number. You have typed these a thousand times. You should never type them again.",
    before: <>A junior lawyer opens a new document. She needs the court name; she types it. The parties; she types them, or searches through old drafts looking for one that has the same parties formatted correctly. The case number she finds it somewhere. The client's CNIC, the other side's address, her own bar number at the bottom are typed again. Every time. For every document.<br /><br />This is not drafting. This is data entry. And it is happening in every firm in Pakistan, every day.</>,
    withLexGat: <>Drafting starts from the case file.<br /><br />Pick the case. Choose the document type. Everything that already exists, like the court, parties, case number, FIR details, lawyer's name and bar number, is already there. You arrive at the legal content immediately.</>,
    pullquote: "You are a lawyer. Write the law. LexGat handles everything else.",
  },
  {
    eyebrow: "The core of the system",
    heading: "Every document you have ever filed in a case. All in one place. All connected.",
    before: <>A plaint is filed in March. The written statement arrives in May.<br />An application is filed in July. Arguments come later. Execution follows.<br /><br />Everything goes into the same file.<br /><br />Pages keep adding up. Documents stack on top of each other.<br /><br />Unless something is clearly flagged, finding a specific document means flipping through the entire file.<br /><br />So every time someone returns to the case<br />or picks it up for the first time, they spend time just locating what already exists</>,
    withLexGat: <>Every document belongs to the same case file.<br /><br />When you start a new draft, the system already knows the parties, the court, the case type, the history of what has been filed, and what stage the matter has reached. Every new document is built on what already exists.<br /><br />No duplication. No inconsistency between documents. No explaining the same facts five times.</>,
    pullquote: "One case. One file. Everything that belongs together, together.",
  },
  {
    eyebrow: "Context-aware AI",
    heading: "The AI in LexGat already knows your case. You do not explain it. You do not re-enter the facts. You just ask.",
    before: <>A lawyer opens an AI tool. He types out the case background. The parties. The stage it is at. What has already been filed. What he needs now.<br /><br />The AI responds. He reviews the output, corrects the parts where the context was misunderstood, adjusts the parts where it got the facts slightly wrong, and reformats the parts that do not match Pakistani court practice.<br /><br />He does this every time. Because the AI does not remember. Every session starts from nothing.</>,
    withLexGat: <>The AI lives inside the case file.<br /><br />It already knows when the plaint was filed. What the case type is. What court it is before. What has already been drafted. What stage the matter is at procedurally. What the next step is under Pakistani law.<br /><br />When you ask for a written statement, it writes for this defendant, in this case, against this plaint. When you ask for an application for interim injunction, it applies Order XXXIX Rules 1 and 2 to this specific set of facts, because it knows the facts.</>,
    pullquote: "Ask. It already knows the rest.",
  },
  {
    eyebrow: "Hearing records",
    heading: "What happened in court today should not live in your memory or on a torn page.",
    before: <>A hearing ends. The lawyer writes a note, whether on paper, in a diary, or in a WhatsApp message to himself. What the judge said. What was ordered. What the next date is. What it is fixed for.<br /><br />A month later, when the case comes up again, that note may or may not be findable. The continuity of the matter exists in the lawyer's memory and nowhere else.</>,
    withLexGat: <>Every proceeding is recorded in the case. What happened. The result. The next date. What the hearing is fixed for.<br /><br />Each entry builds on the last. The history of the matter is always there, ordered, legible, and complete. The lawyer who handled the last three hearings and the associate stepping in for the next one see exactly the same case.</>,
    pullquote: "Continuity is not something you maintain. LexGat maintains it for you.",
  },
  {
    eyebrow: "For partners and senior lawyers",
    heading: "You should not have to ask what is happening in your own firm.",
    before: <>A partner wants an update on a case. He asks the associate. The associate checks with the clerk. The clerk finds the file. The answer comes back accurate enough, but delayed, filtered through two people, and dependent on everyone being reachable.<br /><br />Multiply this by forty cases and three associates. This is a morning meeting that takes forty minutes and should not exist.</>,
    withLexGat: <>Open the dashboard.<br /><br />Every case in the firm. Every stage. Every next date. Every status. The cases that need attention today are flagged. The matters approaching a deadline are surfaced. The ones that have a hearing tomorrow are visible without asking anyone anything.</>,
    pullquote: "Your practice should be visible to you at all times. With LexGat, it is.",
  },
  {
    eyebrow: "Search and retrieval",
    heading: "The draft you are looking for is not in a folder. It is in the case.",
    before: <>A lawyer needs to find a draft application filed six months ago in a particular matter. He searches folders. Opens files that look right but are not. Relies on the file naming convention from six months ago that no longer makes sense to him now.<br /><br />The document exists. Finding it is the problem.</>,
    withLexGat: <>Every document is inside the case it belongs to. There are no folders to search. There are no naming conventions to remember.<br /><br />Open the case. Every document ever drafted or filed in that matter is there, in the order it was created, labelled by type and stage, accessible in two taps.</>,
    pullquote: "Stop searching. Start opening.",
  },
  {
    eyebrow: "Procedural guidance",
    heading: "\"What should I do next?\" is a question LexGat answers before you ask it.",
    before: <>A lawyer returns to a case after a hearing. The judge adjourned for written statements. What is the deadline? What should the written statement address? What comes after that? These answers come from experience, from memory, from asking a senior colleague.</>,
    withLexGat: <>The system knows the stage. It knows the last proceeding. It knows what Pakistani civil procedure requires next under the Code of Civil Procedure 1908.<br /><br />It does not wait for you to ask. It surfaces the next step, the applicable law, and the document you need to draft automatically, as part of the case file.</>,
    pullquote: "The next right action is already waiting for you when you open the case.",
  }
];

export function HowItWorksRealLife() {
  return (
    <div className="bg-[#fafafa] text-zinc-900 border-t border-zinc-200">
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 py-1.5 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-bold tracking-widest uppercase text-zinc-500 mb-8"
            >
              How it works in real life
            </motion.div>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold tracking-tight text-zinc-900 leading-[1.1] mb-8 text-balance"
            >
              Every problem you have been solving manually, <span className="text-zinc-400">LexGat solves automatically.</span>
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-3xl text-zinc-400 font-medium max-w-3xl mx-auto text-balance"
            >
              Not because it stores your data. Because it understands your practice.
            </motion.p>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-32 text-center text-lg md:text-xl text-zinc-600 leading-relaxed font-medium bg-white p-10 rounded-3xl border border-zinc-200 shadow-sm"
          >
            <p className="mb-6 text-zinc-900 text-2xl md:text-3xl font-bold tracking-tight text-balance">
              Most tools give you a place to put things. LexGat gives you a system that thinks.
            </p>
            <p className="text-zinc-500">
              There is a difference between software that holds your information and software that acts on it. LexGat does the second thing. Every case, every document, every deadline, every proceeding is connected. Understood. Always one step ahead of where you are.
            </p>
          </motion.div>

          <div className="space-y-40">
            {SECTIONS.map((section, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="grid lg:grid-cols-12 gap-12 lg:gap-24 items-start"
              >
                <div className="lg:col-span-5 lg:sticky lg:top-32">
                  <div className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-6 flex items-center gap-4">
                    <span className="w-8 h-px bg-zinc-300"></span>
                    {section.eyebrow}
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-10 leading-[1.2] text-balance">
                    {section.heading}
                  </h3>
                </div>
                
                <div className="lg:col-span-7 space-y-12">
                  {/* Before */}
                  <div className="bg-zinc-100 rounded-3xl p-8 border border-zinc-200/60">
                    <h4 className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                       <span className="w-2 h-2 rounded-full bg-zinc-300"></span> Before
                    </h4>
                    <p className="text-lg text-zinc-600 leading-relaxed">
                      {section.before}
                    </p>
                  </div>
                  
                  {/* With LexGat */}
                  <div className="bg-white rounded-3xl p-8 md:p-10 border border-zinc-200 shadow-xl shadow-zinc-200/50 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-zinc-900"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-zinc-100 rounded-full blur-[60px] opacity-50 -mr-16 -mt-16 pointer-events-none"></div>
                    
                    <h4 className="text-sm font-bold text-zinc-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-zinc-900 animate-pulse"></span> With LexGat
                    </h4>
                    <p className="text-lg md:text-xl text-zinc-900 leading-relaxed font-medium mb-8">
                      {section.withLexGat}
                    </p>
                    
                    <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-6 relative">
                       <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-2xl"></div>
                       <p className="text-xl md:text-2xl font-bold text-blue-600 leading-tight">
                         "{section.pullquote}"
                       </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-32 px-6 bg-zinc-900 text-white border-t border-zinc-800 text-center relative overflow-hidden">
         <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(255,255,255,0.03),transparent_50%)]"></div>
         
         <div className="max-w-4xl mx-auto relative z-10">
           <motion.h2 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8 }}
             className="text-5xl md:text-7xl font-bold tracking-tight mb-12 text-balance"
           >
             LexGat does not just store your practice. <span className="text-zinc-500">It understands it.</span>
           </motion.h2>
           
           <motion.div 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8, delay: 0.2 }}
             className="text-xl md:text-2xl text-zinc-400 font-medium leading-relaxed max-w-3xl mx-auto mb-16 space-y-6"
           >
             <p>
               Every case file is a living record, connected to your diary, your documents, your proceedings, and your AI drafting assistant. Everything that belongs to a case is in the case. Everything that needs to happen next is surfaced automatically.
             </p>
             <p className="text-white font-bold">
               This is what it means to have your entire practice in one place.
             </p>
             <p className="text-lg">
               Not a folder. Not a diary. Not a collection of disconnected tools.
             </p>
             <p className="text-2xl text-white font-bold mt-8">
               One system. Every case. Total clarity.
             </p>
           </motion.div>
           
           <motion.button 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              onClick={() => { window.location.href = appUrl('/login'); }}
              className="bg-white text-zinc-900 px-10 py-5 rounded-full font-medium hover:bg-zinc-100 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 text-lg"
            >
              Start your free trial, no card required
            </motion.button>
            <div className="mt-6 text-sm text-zinc-500 font-medium">Set up in one afternoon. Feels different by morning.</div>
         </div>
      </section>
    </div>
  );
}

export default HowItWorksRealLife;
